import { Check, Languages } from "lucide-react";
import { cn } from "@ascleo/ui/lib/utils";
import { FRONTDESK_LANGUAGE_CATALOG } from "../../../lib/i18n/language-catalog";
import { useLanguageFlag } from "../../../components/language-selector";
import { Tile } from "./Tile";

interface LanguageTilesProps {
  /** Catalog codes to offer, in catalog order. */
  readonly codes: readonly string[];
  /** Stable test-id prefix; each tile appends `-{code}-button`. */
  readonly testIdPrefix: string;
  readonly onSelect: (code: string) => void;
  /** The language currently in use; marked with a check and `aria-pressed` (KO-35). */
  readonly selectedCode?: string;
}

/**
 * Touch-sized language choices rendered as kiosk band tiles with each language's
 * autonym and, where it names the speakers correctly, its flag. Languages
 * without a flag get a neutral language glyph of the same size, so every row
 * aligns (KO-27). The grid is content-sized (two columns on narrow screens).
 *
 * @since 1.1.8
 * @example
 * ```tsx
 * <LanguageTiles codes={["de", "tr"]} testIdPrefix="frontdesk-language-entry" selectedCode="de" onSelect={choose} />
 * ```
 */
export function LanguageTiles({ codes, testIdPrefix, onSelect, selectedCode }: LanguageTilesProps) {
  const flagFor = useLanguageFlag();
  return (
    <div className="grid min-w-0 auto-rows-min grid-cols-2 gap-3.5 md:grid-cols-3 lg:grid-cols-4">
      {FRONTDESK_LANGUAGE_CATALOG.filter(({ code }) => codes.includes(code)).map(({ code, label }) => {
        const Flag = flagFor(code);
        const selected = selectedCode !== undefined && selectedCode.split("-")[0] === code;
        return (
          <Tile
            key={code}
            tone={selected ? "blue-subtle" : "paper"}
            size="band"
            aria-pressed={selected}
            className={cn("min-h-20 border-2", selected ? "border-primary" : "border-border")}
            data-testid={`${testIdPrefix}-${code}-button`}
            name={
              <span className="flex items-center gap-2">
                <span lang={code} dir="auto" className="min-w-0 flex-1">{label}</span>
                {selected ? <Check aria-hidden className="size-6 shrink-0" /> : null}
              </span>
            }
            icon={Flag ? <Flag className="rounded-sm" /> : <Languages />}
            onClick={() => onSelect(code)}
          />
        );
      })}
    </div>
  );
}
